/**
 * Payment API Service for AIPrice
 * Calls local Express.js payment server instead of Python backend
 */

// API Configuration - Updated to use standard API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/**
 * API Response wrapper
 * @param {Response} response - Fetch response
 * @returns {Promise<Object>} Parsed JSON response
 */
const handleApiResponse = async (response) => {
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || `API Error: ${response.status}`);
  }

  return data;
};

/**
 * Make API request with proper error handling
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} API response
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultOptions = {
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...options,
  };

  try {
    console.log(`🔄 API Request: ${options.method || "GET"} ${url}`);

    const response = await fetch(url, defaultOptions);
    const result = await handleApiResponse(response);

    console.log(`✅ API Response:`, result);
    return result;
  } catch (error) {
    console.error(`❌ API Error: ${options.method || "GET"} ${url}`, error);
    throw error;
  }
};

/**
 * Create payment link for a plan
 * @param {Object} planData - Plan data
 * @param {string} planData.userId - User ID
 * @param {number} planData.planId - Plan ID (0-4)
 * @param {string} planData.planName - Plan name
 * @param {number} planData.amount - Amount in VND
 * @param {string} [planData.returnUrl] - Custom return URL
 * @param {string} [planData.cancelUrl] - Custom cancel URL
 * @param {string} [planData.promoCode] - Promo code for discount
 * @returns {Promise<Object>} Payment response with checkout URL
 */
export const createPayment = async (planData) => {
  return apiRequest("/api/payment/create", {
    method: "POST",
    body: JSON.stringify(planData),
  });
};

/**
 * Verify payment status
 * @param {Object} params - Verification parameters
 * @param {string} [params.orderId] - Order ID
 * @param {string} [params.orderCode] - Order code
 * @param {string} [params.paymentLinkId] - Payment link ID
 * @returns {Promise<Object>} Verification result
 */
export const verifyPayment = async (params) => {
  const queryString = new URLSearchParams(params).toString();
  return apiRequest(`/api/payment/verify?${queryString}`);
};

/**
 * Get payment status by payment ID
 * @param {string} paymentId - Payment ID
 * @returns {Promise<Object>} Payment status
 */
export const getPaymentStatus = async (paymentId) => {
  return apiRequest(`/api/payment/status/${paymentId}`);
};

/**
 * Get payment history for user
 * @param {string} userId - User ID
 * @param {Object} [options] - Query options
 * @param {number} [options.limit=10] - Limit results
 * @param {number} [options.page=1] - Page number
 * @returns {Promise<Object>} Payment history
 */
export const getPaymentHistory = async (userId, options = {}) => {
  const { limit = 10, page = 1 } = options;
  const queryString = new URLSearchParams({ limit, page }).toString();
  return apiRequest(`/api/payment/history/${userId}?${queryString}`);
};

/**
 * Cancel a pending payment
 * @param {string} paymentId - Payment ID
 * @param {string} [reason] - Cancellation reason
 * @returns {Promise<Object>} Cancellation result
 */
export const cancelPayment = async (paymentId, reason = "User cancelled") => {
  return apiRequest(`/api/payment/cancel/${paymentId}`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
};

/**
 * Test API connectivity
 * @returns {Promise<Object>} Health check result
 */
export const testApiConnection = async () => {
  return apiRequest("/api/health");
};

/**
 * Get API information
 * @returns {Promise<Object>} API info
 */
export const getApiInfo = async () => {
  return apiRequest("/api");
};

/**
 * Enhanced createPayOSPayment function for backward compatibility
 * Replaces the existing function in PricingScreen and LandingPage2
 * @param {Object} plan - Plan object from UI
 * @returns {Promise<void>} Redirects to PayOS checkout
 */
export const createPayOSPayment = async (plan) => {
  try {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      throw new Error("User ID không tồn tại. Vui lòng đăng nhập lại.");
    }

    // Extract price from plan object
    let amount = 0;
    if (
      plan.price &&
      typeof plan.price === "string" &&
      plan.price !== "Miễn phí"
    ) {
      amount = parseInt(plan.price.replace(/[^\d]/g, "")) || 0;
    }

    // Prepare payment data
    const paymentData = {
      userId,
      planId: plan.id,
      planName: plan.name,
      amount,
      returnUrl: `${window.location.origin}/payment/success`,
      cancelUrl: `${window.location.origin}/payment/cancel`,
    };

    console.log("🔄 Creating PayOS payment:", paymentData);

    // Handle demo plan (free)
    if (plan.id === 0 || amount === 0) {
      console.log("✅ Demo plan accessed for free");

      // For demo plan, just show success message
      alert("Gói demo đã được kích hoạt miễn phí!");
      return;
    }

    // Create payment
    const result = await createPayment(paymentData);

    if (result.success && result.data?.paymentUrl) {
      // Save payment info to localStorage
      try {
        localStorage.setItem(
          "latestPaymentData",
          JSON.stringify({
            amount,
            planId: plan.id,
            planName: plan.name,
            orderCode: result.data.orderCode,
            timestamp: new Date().getTime(),
          })
        );
      } catch (e) {
        console.error("Error saving payment data to localStorage:", e);
      }

      // Redirect to PayOS checkout
      console.log("🔗 Redirecting to PayOS:", result.data.paymentUrl);
      window.location.href = result.data.paymentUrl;
    } else {
      throw new Error(result.message || "Không thể tạo liên kết thanh toán");
    }
  } catch (error) {
    console.error("❌ Error creating PayOS payment:", error);

    // User-friendly error messages
    let errorMessage =
      "Có lỗi xảy ra khi tạo thanh toán, vui lòng thử lại sau.";

    if (error.message.includes("không tồn tại")) {
      errorMessage = "Vui lòng đăng nhập để sử dụng dịch vụ.";
    } else if (error.message.includes("không hợp lệ")) {
      errorMessage = "Thông tin gói dịch vụ không hợp lệ.";
    } else if (
      error.message.includes("network") ||
      error.message.includes("fetch")
    ) {
      errorMessage = "Lỗi kết nối mạng. Vui lòng kiểm tra kết nối internet.";
    }

    alert(errorMessage);
    throw error;
  }
};

/**
 * Check if payment server is running
 * @returns {Promise<boolean>} Server status
 */
export const checkPaymentServerStatus = async () => {
  try {
    await testApiConnection();
    return true;
  } catch (error) {
    console.warn("❌ Payment server not available:", error.message);
    return false;
  }
};

// Export default object for easier imports
const paymentApi = {
  createPayment,
  verifyPayment,
  getPaymentStatus,
  getPaymentHistory,
  cancelPayment,
  testApiConnection,
  getApiInfo,
  createPayOSPayment,
  checkPaymentServerStatus,
};

export default paymentApi;
